import { createContext, useEffect, type ReactNode } from 'react'
import {
  cognitoSignIn,
  cognitoSignOut,
  cognitoGetCurrentUser,
} from '@/lib/cognito'
import {
  loginWithProvider,
  exchangeCodeForTokens,
  parseIdToken,
} from '@/lib/cognitoSocial'
import { useAuthStore } from '@/store/authStore'
import { setAuthHint, clearAuthHint } from '@/lib/auth/authHintCookie'
import type {
  AuthUser,
  LoginCredentials,
  UserRole,
} from '@/types/auth.types'

// ─────────────────────────────────────────────────────────────
// Context type
// ─────────────────────────────────────────────────────────────

export interface AuthContextValue {
  user:            AuthUser | null
  isLoading:       boolean
  isAuthenticated: boolean

  login:             (credentials: LoginCredentials) => Promise<AuthUser>
  loginWithGoogle:   () => void
  loginWithMeta:     () => void
  logout:            () => void
  handleOAuthCallback: (code: string) => Promise<AuthUser>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

// ─────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, isLoading, setUser, setLoading, clearAuth } = useAuthStore()

  // On mount: restore session from Cognito localStorage
  useEffect(() => {
    setLoading(true)
    cognitoGetCurrentUser()
      .then((restored) => setUser(restored))
      .catch(() => clearAuth())
      .finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Sincronizza l'hint di sessione cross-origin (cookie su .ollylife.com) così il
  // sito pubblico sa mostrare "Dashboard" invece di "Accedi". Solo a sessione
  // risolta (evita di azzerare il cookie durante il restore iniziale).
  useEffect(() => {
    if (isLoading) return
    if (user) setAuthHint(user.role, user.email)
    else clearAuthHint()
  }, [user, isLoading])

  // ── Actions ──────────────────────────────────────────────────

  async function login(credentials: LoginCredentials): Promise<AuthUser> {
    const result = await cognitoSignIn(credentials)

    // MFA challenge — caller must handle this case
    if ('mfaRequired' in result) {
      throw Object.assign(new Error('MFA_REQUIRED'), result)
    }

    setUser(result)
    return result
  }

  function loginWithGoogle() {
    loginWithProvider('Google')
  }

  function loginWithMeta() {
    loginWithProvider('Facebook')
  }

  function logout() {
    cognitoSignOut()
    clearAuth()
  }

  async function handleOAuthCallback(code: string): Promise<AuthUser> {
    const tokens = await exchangeCodeForTokens(code)
    const payload = parseIdToken(tokens.id_token)

    const groups = (payload['cognito:groups'] as string[]) ?? []
    let role: UserRole = 'USER'
    if (groups.includes('Admins'))     role = 'ADMIN'
    else if (groups.includes('Operators')) role = 'OPERATOR'

    const name      = (payload['name'] as string) ?? (payload['email'] as string)
    const given     = payload['given_name']  as string | undefined
    const family    = payload['family_name'] as string | undefined
    const [fnFromName, ...lnRest] = (name ?? '').split(' ')

    const authUser: AuthUser = {
      sub:          payload['sub'] as string,
      email:        payload['email'] as string,
      name,
      firstName:    given  || fnFromName       || undefined,
      lastName:     family || lnRest.join(' ') || undefined,
      role,
      accessToken:  tokens.access_token,
      idToken:      tokens.id_token,
      refreshToken: tokens.refresh_token,
      tokenExpiry:  (Date.now() / 1000 + tokens.expires_in) * 1000,
    }

    setUser(authUser)
    return authUser
  }

  // ── Value ─────────────────────────────────────────────────────

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    loginWithGoogle,
    loginWithMeta,
    logout,
    handleOAuthCallback,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
